import type { ClientDeviceWithDevice } from '@maru44/huntre-utils/src/models/clientDevice'
import type { ClientSubGroup } from '@maru44/huntre-utils/src/models/clientSubGroup'
import { IsTrappingStatus, OperationStatus } from '@maru44/huntre-utils/src/models/device'
import SearchIcon from '@mui/icons-material/Search'
import {
  Box,
  Chip,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-hot-toast'
import { Link } from 'react-router-dom'
import { StatusChip } from 'src/components/atoms/StatusChip'
import { LastSeen } from 'src/components/device/LastSeen'
import { PopoverWhatIsTurnedOff } from 'src/components/device/PopoverIsTurnedOff'
import { ClientDeviceSwitch } from 'src/components/dialogs/device/ClientDeviceSwitch'
import type { KeyedMutator } from 'swr'

const StatusFilter = {
  All: 'all',
  Trapping: 'trapping',
  Abnormal: 'abnormal',
  NotifyOn: 'notifyOn',
  NotifyOff: 'notifyOff',
} as const
type StatusFilter = (typeof StatusFilter)[keyof typeof StatusFilter]

const SortKey = {
  LastMessageDesc: 'lastMessageDesc',
  LastMessageAsc: 'lastMessageAsc',
  DeviceIdAsc: 'deviceIdAsc',
  DeviceIdDesc: 'deviceIdDesc',
} as const
type SortKey = (typeof SortKey)[keyof typeof SortKey]

const statusFilterLabel = (v: StatusFilter): string => {
  switch (v) {
    case StatusFilter.All:
      return 'すべて'
    case StatusFilter.Trapping:
      return '作動中'
    case StatusFilter.Abnormal:
      return '異常あり'
    case StatusFilter.NotifyOn:
      return '通知オン'
    case StatusFilter.NotifyOff:
      return '通知オフ'
  }
}

const sortKeyLabel = (v: SortKey): string => {
  switch (v) {
    case SortKey.LastMessageDesc:
      return '最終通信が新しい順'
    case SortKey.LastMessageAsc:
      return '最終通信が古い順'
    case SortKey.DeviceIdAsc:
      return 'デバイスID(昇順)'
    case SortKey.DeviceIdDesc:
      return 'デバイスID(降順)'
  }
}

const matchStatusFilter = (d: ClientDeviceWithDevice, filter: StatusFilter): boolean => {
  switch (filter) {
    case StatusFilter.All:
      return true
    case StatusFilter.Trapping:
      return d.device.isTrapping
    case StatusFilter.Abnormal:
      return d.device.operationStatus === OperationStatus.Abnormal
    case StatusFilter.NotifyOn:
      return !d.clientDevice.isTurnedOff
    case StatusFilter.NotifyOff:
      return d.clientDevice.isTurnedOff
  }
}

const compareDevices = (a: ClientDeviceWithDevice, b: ClientDeviceWithDevice, sortKey: SortKey): number => {
  switch (sortKey) {
    case SortKey.DeviceIdAsc:
      return a.clientDevice.deviceId.localeCompare(b.clientDevice.deviceId)
    case SortKey.DeviceIdDesc:
      return b.clientDevice.deviceId.localeCompare(a.clientDevice.deviceId)
    default: {
      const ta = a.device.lastMessageReceivedAt?.toMillis() ?? null
      const tb = b.device.lastMessageReceivedAt?.toMillis() ?? null
      // 通信なしは常に末尾
      if (ta === null && tb === null) return 0
      if (ta === null) return 1
      if (tb === null) return -1
      return sortKey === SortKey.LastMessageAsc ? ta - tb : tb - ta
    }
  }
}

type DeviceTableProps = {
  clientDevices: ClientDeviceWithDevice[] | undefined
  clientSubGroups: ClientSubGroup[]
  onUpdateIsTurnedOff: (id: string, isTurnedOff: boolean) => Promise<void>
  mutate: KeyedMutator<ClientDeviceWithDevice[]>
}

export const ClientDeviceTable = ({ clientDevices, clientSubGroups, onUpdateIsTurnedOff, mutate }: DeviceTableProps) => {
  const showSubGroupColumn = clientSubGroups.length > 0

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(StatusFilter.All)
  const [sortKey, setSortKey] = useState<SortKey>(SortKey.LastMessageDesc)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)

  // 絞り込み条件が変わったら先頭ページに戻す
  useEffect(() => {
    setPage(0)
  }, [search, statusFilter, sortKey, rowsPerPage])

  const filtered = useMemo(() => {
    if (!clientDevices) return []
    const keyword = search.trim().toLowerCase()
    return clientDevices
      .filter((d) => (keyword === '' ? true : d.clientDevice.deviceId.toLowerCase().includes(keyword)))
      .filter((d) => matchStatusFilter(d, statusFilter))
      .slice()
      .sort((a, b) => compareDevices(a, b, sortKey))
  }, [clientDevices, search, statusFilter, sortKey])

  const paged = useMemo(() => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [filtered, page, rowsPerPage])

  const total = clientDevices?.length ?? 0

  return (
    <Stack spacing={1.5}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{
          alignItems: { sm: 'center' },
        }}
      >
        <TextField
          size="small"
          placeholder="デバイスIDで検索"
          id="search"
          name="search"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          sx={{ minWidth: { sm: 220 }, flexGrow: 1 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <TextField
          select
          size="small"
          label="状態で絞り込み"
          id="statusFilter"
          name="statusFilter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          sx={{ minWidth: 160 }}
        >
          {Object.values(StatusFilter).map((v) => (
            <MenuItem key={v} value={v}>
              {statusFilterLabel(v)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="並べ替え"
          id="sortKey"
          name="sortKey"
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value as SortKey)}
          sx={{ minWidth: 200 }}
        >
          {Object.values(SortKey).map((v) => (
            <MenuItem key={v} value={v}>
              {sortKeyLabel(v)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
        }}
      >
        {filtered.length === total ? `全${total}件` : `${filtered.length}件 / 全${total}件`}
      </Typography>

      <TableContainer component={Paper} elevation={6}>
        <Table sx={{ minWidth: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography variant="subtitle1">デバイス</Typography>
              </TableCell>
              <TableCell>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography variant="subtitle1">通知</Typography>
                  <PopoverWhatIsTurnedOff />
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle1">罠状況 / 異常</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle1">最終通信日時</Typography>
              </TableCell>
              {showSubGroupColumn && (
                <TableCell>
                  <Typography variant="subtitle1">サブグループ</Typography>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {paged.length > 0 ? (
              paged.map((d) => (
                <DeviceRow
                  device={d}
                  key={d.device.id}
                  clientSubGroups={clientSubGroups}
                  showSubGroupColumn={showSubGroupColumn}
                  onUpdate={onUpdateIsTurnedOff}
                  mutate={mutate}
                />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={showSubGroupColumn ? 5 : 4}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      textAlign: 'center',
                      py: 2,
                    }}
                  >
                    該当するデバイスがありません
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, next) => setPage(next)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
          rowsPerPageOptions={[10, 20, 50, 100]}
          labelRowsPerPage="表示件数"
        />
      </TableContainer>
    </Stack>
  )
}

type DeviceRowProps = {
  device: ClientDeviceWithDevice
  clientSubGroups: ClientSubGroup[]
  showSubGroupColumn: boolean
  onUpdate: (id: string, isTurnedOff: boolean) => Promise<void>
  mutate: KeyedMutator<ClientDeviceWithDevice[]>
}

const DeviceRow = ({ device, clientSubGroups, showSubGroupColumn, onUpdate, mutate }: DeviceRowProps) => {
  const trappingStatus = useMemo((): IsTrappingStatus => {
    if (device.device.isTrapping) return IsTrappingStatus.Trapping
    return IsTrappingStatus.NotTrapping
  }, [device.device.isTrapping])

  const matchingSubGroups = useMemo(
    () => clientSubGroups.filter((sg) => sg.clientDeviceIds.includes(device.clientDevice.id)),
    [clientSubGroups, device.clientDevice.id],
  )

  const handleUpdate = useCallback(async () => {
    const deviceId = device.clientDevice.deviceId
    const nextIsTurnedOff = !device.clientDevice.isTurnedOff
    try {
      await onUpdate(device.clientDevice.id, nextIsTurnedOff)
      toast.success(`${deviceId}: 通知を${nextIsTurnedOff ? 'off' : 'on'}にしました`)
      await mutate()
    } catch {
      toast.error(`${deviceId}: 通知の変更に失敗しました`)
    }
  }, [device.clientDevice, onUpdate, mutate])

  return (
    <TableRow>
      <TableCell component="th" scope="row">
        <Link to={`${device.clientDevice.id}`}>{device.clientDevice.deviceId}</Link>
      </TableCell>
      <TableCell>
        <ClientDeviceSwitch clientDevice={device.clientDevice} onUpdate={handleUpdate} />
      </TableCell>
      <TableCell>
        {!device.clientDevice.isTurnedOff ? (
          <Stack
            direction="row"
            spacing={0.5}
            useFlexGap
            sx={{
              flexWrap: 'wrap',
            }}
          >
            <StatusChip status={trappingStatus} />
            {device.device.operationStatus === OperationStatus.Abnormal && <StatusChip status={OperationStatus.Abnormal} />}
          </Stack>
        ) : (
          <Chip label="通知オフ" size="small" variant="outlined" />
        )}
      </TableCell>
      <TableCell>
        <LastSeen timestamp={device.device.lastMessageReceivedAt} />
      </TableCell>
      {showSubGroupColumn && (
        <TableCell>
          <Stack
            direction="row"
            spacing={0.5}
            useFlexGap
            sx={{
              flexWrap: 'wrap',
            }}
          >
            {matchingSubGroups.length > 0 ? (
              matchingSubGroups.map((sg) => <Chip key={sg.id} label={sg.name} size="small" variant="outlined" />)
            ) : (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                -
              </Typography>
            )}
          </Stack>
        </TableCell>
      )}
    </TableRow>
  )
}
